import {
  Box,
  Flex,
  Button,
  Input,
  FormLabel,
  Center,
  Divider,
  Text,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import Spreadsheet from 'react-spreadsheet';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import {
  createMatrixRow,
  createEmptyCell,
  createDefaultData,
  PrimitiveCellType,
  MatrixType,
  translateDataCellObjectsToSpreadsheet,
  translateSpreadsheetToDataCellObjects,
} from './spreadsheet.utils';
import { CustomDataEditor } from './SpreadsheetDataEditor';
import { CustomDataViewer } from './SpreadsheetDataViewer';
import { useMousedownListeners } from './useMousedownListeners';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import { FiSave, FiPlusCircle, FiMinusCircle } from 'react-icons/fi';

import './reportingSpreadsheet.scss';

type DataCellObjectConfig = CreditAgreement.Config.Reporting.Outputs.Xlsx.DataCellObjectConfig;

type SpreadsheetIconBarProps = {
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  sheetIdxConfigPropertyKey: string;
  dataMatrix: MatrixType;
  setDataMatrix: React.Dispatch<React.SetStateAction<MatrixType>>;
};
const SpreadsheetIconBar = ({
  wipConfig,
  sheetIdxConfigPropertyKey,
  dataMatrix,
  setDataMatrix,
}: SpreadsheetIconBarProps) => {
  const toast = useToast();

  const onSave = (): void => {
    if (!wipConfig.isMutateAllowed(sheetIdxConfigPropertyKey)) {
      return;
    }

    const cellObjects = translateSpreadsheetToDataCellObjects(dataMatrix);
    const validateErrors = reportingValidate.output.validateDataCellObjectConfig(
      { data: cellObjects },
    );
    if (validateErrors.length > 0) {
      toast({
        title: 'Spreadsheet Edit Errors',
        description: validateErrors.join(', '),
        status: 'error',
        position: 'top',
        duration: 8000,
        isClosable: true,
      });
      return;
    }

    const updatedDataCellConfig: DataCellObjectConfig = {
      data: cellObjects,
    };
    wipConfig.formProps.setFieldValue(
      sheetIdxConfigPropertyKey,
      updatedDataCellConfig,
    );
  };

  const addColumn = (): void => {
    setDataMatrix((prevData) => {
      const copyData = _.cloneDeep(prevData);
      copyData.forEach((row) => {
        row.push(createEmptyCell());
      });
      return copyData;
    });
  };

  const addRow = (): void => {
    setDataMatrix((prevData) => {
      const copyData = _.cloneDeep(prevData);
      const newRow = createMatrixRow(copyData[0].length);
      copyData.push(newRow);
      return copyData;
    });
  };

  const removeColumn = (): void => {
    if (dataMatrix[0].length === 0) {
      return;
    }

    setDataMatrix((prevData) => {
      const copyData = _.cloneDeep(prevData);
      copyData.forEach((row) => {
        row.pop();
      });
      return copyData;
    });
  };

  const removeRow = (): void => {
    if (dataMatrix.length === 0) {
      return;
    }

    setDataMatrix((prevData) => {
      const copyData = _.cloneDeep(prevData);
      copyData.pop();
      return copyData;
    });
  };

  return (
    <Flex id="spreadsheet-icon-bar" mb="2">
      <Button px="2" onClick={onSave} leftIcon={<FiSave />}>
        Save Data
      </Button>

      <Center h="10" mx="5">
        <Divider orientation="vertical" />
      </Center>

      <Button mr="2" px="2" onClick={addColumn} leftIcon={<FiPlusCircle />}>
        Add Column
      </Button>

      <Button mr="2" px="2" onClick={addRow} leftIcon={<FiPlusCircle />}>
        Add Row
      </Button>

      <Button mr="2" px="2" onClick={removeColumn} leftIcon={<FiMinusCircle />}>
        Remove Column
      </Button>

      <Button mr="2" px="2" onClick={removeRow} leftIcon={<FiMinusCircle />}>
        Remove Row
      </Button>
    </Flex>
  );
};

const _helpGenerateMatrix = (
  data: DataCellObjectConfig['data'],
): MatrixType => {
  if (data.length === 0) {
    return createDefaultData();
  }
  return translateDataCellObjectsToSpreadsheet(data);
};

type ReportSpreadsheetConfigViewProps = {
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  sheetIdxConfigPropertyKey: string;
  targetSheetConfig: DataCellObjectConfig;
  // eslint-disable-next-line react/no-unused-prop-types
  queryResults: CreditAgreement.Config.QueryResult[];
};
export const ReportSpreadsheetConfigView = ({
  wipConfig,
  sheetIdxConfigPropertyKey,
  targetSheetConfig,
}: ReportSpreadsheetConfigViewProps) => {
  const spreadsheetContainerRef = useRef<HTMLDivElement>(null);

  const [dataMatrixState, setDataMatrixState] = useState<MatrixType>(
    _helpGenerateMatrix(targetSheetConfig.data),
  );

  useEffect(() => {
    setDataMatrixState(_helpGenerateMatrix(targetSheetConfig.data));
  }, [targetSheetConfig.data]);

  const [focusedCellIdx, setFocusedCellIdx] = useState<{
    row: number;
    column: number;
  } | null>(null);

  const focusedCellValue = useMemo<string>(() => {
    if (!focusedCellIdx) {
      return '';
    }
    const { row, column } = focusedCellIdx;
    const targetCell = dataMatrixState[row][column];
    if (!targetCell || _.isNil(targetCell.value)) {
      return '';
    }
    return String(targetCell.value);
  }, [dataMatrixState, focusedCellIdx]);

  useMousedownListeners(spreadsheetContainerRef, {
    outsideClickFn: () => {
      setFocusedCellIdx(null);
    },
  });

  // As this is an internal component (parent component already manages permissions),
  // just utilizing clear safekeeping here on not rendering this component if
  // mutate permission is not allowed.
  // We're currently only allowing the "Data Cell Mode" for viewing purposes,
  // no need for spreadsheets just to view the data set
  if (!wipConfig.isMutateAllowed(sheetIdxConfigPropertyKey)) {
    return null;
  }

  return (
    <Box id="reporting-spreadsheet">
      <SpreadsheetIconBar
        wipConfig={wipConfig}
        sheetIdxConfigPropertyKey={sheetIdxConfigPropertyKey}
        dataMatrix={dataMatrixState}
        setDataMatrix={setDataMatrixState}
      />

      <Text fontStyle="italic" fontSize="sm" mb="3">
        Make sure to &quot;Save Data&quot; after applying any spreadsheet
        updates
      </Text>

      <Box ref={spreadsheetContainerRef} w="fit-content">
        <Flex id="formula-bar-contrainer" mb="2" alignItems="center">
          <FormLabel as="i" mr="1" my="0">
            fx
            {focusedCellIdx
              ? ` (${commonUtils.numberToExcelLetter(focusedCellIdx.column)}${
                  focusedCellIdx.row + 1
                })`
              : undefined}
            :
          </FormLabel>
          <Input
            px="2"
            w="3xl"
            value={focusedCellValue}
            onChange={(evt) => {
              if (!focusedCellIdx) {
                return;
              }

              const clonedMatrix = _.cloneDeep(dataMatrixState);
              const { row, column } = focusedCellIdx;
              const targetCell = clonedMatrix[row][column];
              if (targetCell) {
                targetCell.value = evt.target.value;
                setDataMatrixState(clonedMatrix);
              }
            }}
          />
        </Flex>

        <Box maxW="8xl" maxH="md" overflow="auto">
          <Spreadsheet<PrimitiveCellType>
            className="reporting-spreadsheet"
            DataViewer={CustomDataViewer}
            DataEditor={CustomDataEditor}
            data={dataMatrixState}
            onChange={(matrix) => setDataMatrixState(matrix)}
            onActivate={(props) => {
              setFocusedCellIdx(props);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
